import { create } from 'zustand'
import type { FocusPhase } from '@/lib/focusTypes'
import {
  DEFAULT_WORK_DURATION,
  DEFAULT_BREAK_DURATION,
  LONG_BREAK_DURATION,
} from '@/lib/focusTypes'

interface QueueItem {
  taskId: number
  taskTitle: string
}

interface FocusState {
  isActive: boolean
  taskId: number | null
  taskTitle: string
  phase: FocusPhase
  timeRemaining: number
  isRunning: boolean
  sessionsCompleted: number
  workDuration: number
  breakDuration: number
  showFullOverlay: boolean

  // Task queue for multi-task focus
  taskQueue: QueueItem[]
  currentQueueIndex: number
  completedInSession: number

  startFocus: (taskId: number, taskTitle: string) => void
  startFocusQueue: (tasks: QueueItem[]) => void
  completeCurrentTask: () => void
  togglePause: () => void
  tick: () => void
  skipPhase: () => void
  stopFocus: () => void
  minimizeToFloat: () => void
  maximizeOverlay: () => void
}

export const useFocusStore = create<FocusState>((set, get) => ({
  isActive: false,
  taskId: null,
  taskTitle: '',
  phase: 'idle',
  timeRemaining: DEFAULT_WORK_DURATION,
  isRunning: false,
  sessionsCompleted: 0,
  workDuration: DEFAULT_WORK_DURATION,
  breakDuration: DEFAULT_BREAK_DURATION,

  showFullOverlay: true,

  // Task queue
  taskQueue: [],
  currentQueueIndex: 0,
  completedInSession: 0,

  startFocus: (taskId, taskTitle) => {
    set({
      isActive: true,
      taskId,
      taskTitle,
      phase: 'work',
      timeRemaining: DEFAULT_WORK_DURATION,
      isRunning: true,
      sessionsCompleted: 0,
      showFullOverlay: true,
      taskQueue: [{ taskId, taskTitle }],
      currentQueueIndex: 0,
      completedInSession: 0,
    })
  },

  startFocusQueue: (tasks) => {
    if (tasks.length === 0) return
    const first = tasks[0]
    set({
      isActive: true,
      taskId: first.taskId,
      taskTitle: first.taskTitle,
      phase: 'work',
      timeRemaining: DEFAULT_WORK_DURATION,
      isRunning: true,
      sessionsCompleted: 0,
      showFullOverlay: true,
      taskQueue: tasks,
      currentQueueIndex: 0,
      completedInSession: 0,
    })
  },

  completeCurrentTask: () => {
    const state = get()
    const nextIndex = state.currentQueueIndex + 1
    const newCompleted = state.completedInSession + 1

    if (nextIndex < state.taskQueue.length) {
      const next = state.taskQueue[nextIndex]
      set({
        taskId: next.taskId,
        taskTitle: next.taskTitle,
        currentQueueIndex: nextIndex,
        completedInSession: newCompleted,
      })
    } else {
      // All tasks done
      set({
        isActive: false,
        taskId: null,
        taskTitle: '',
        phase: 'idle',
        timeRemaining: DEFAULT_WORK_DURATION,
        isRunning: false,
        sessionsCompleted: 0,
        taskQueue: [],
        currentQueueIndex: 0,
        completedInSession: newCompleted,
      })
    }
  },

  togglePause: () => {
    set((s) => ({ isRunning: !s.isRunning }))
  },

  tick: () => {
    const state = get()
    if (!state.isRunning || !state.isActive) return

    if (state.timeRemaining <= 1) {
      // Phase transition
      if (state.phase === 'work') {
        const newSessions = state.sessionsCompleted + 1
        const isLongBreak = newSessions % 4 === 0
        set({
          phase: 'break',
          timeRemaining: isLongBreak ? LONG_BREAK_DURATION : DEFAULT_BREAK_DURATION,
          sessionsCompleted: newSessions,
          isRunning: false, // Pause at transition
        })
      } else {
        // Break finished → next work session
        set({
          phase: 'work',
          timeRemaining: DEFAULT_WORK_DURATION,
          isRunning: false, // Pause at transition
        })
      }
    } else {
      set({ timeRemaining: state.timeRemaining - 1 })
    }
  },

  skipPhase: () => {
    const state = get()
    if (state.phase === 'work') {
      const newSessions = state.sessionsCompleted + 1
      const isLongBreak = newSessions % 4 === 0
      set({
        phase: 'break',
        timeRemaining: isLongBreak ? LONG_BREAK_DURATION : DEFAULT_BREAK_DURATION,
        sessionsCompleted: newSessions,
        isRunning: false,
      })
    } else {
      set({
        phase: 'work',
        timeRemaining: DEFAULT_WORK_DURATION,
        isRunning: false,
      })
    }
  },

  stopFocus: () => {
    set({
      isActive: false,
      taskId: null,
      taskTitle: '',
      phase: 'idle',
      timeRemaining: DEFAULT_WORK_DURATION,
      isRunning: false,
      sessionsCompleted: 0,
      showFullOverlay: true,
      taskQueue: [],
      currentQueueIndex: 0,
      completedInSession: 0,
    })
  },

  minimizeToFloat: () => set({ showFullOverlay: false }),
  maximizeOverlay: () => set({ showFullOverlay: true }),
}))
